import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './components/ui/ToastProvider';
import PrivateRoute from './components/PrivateRoute';
import { WorkflowEditorProvider } from './components/WorkflowEditorHost';
import './App.css';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Chat = lazy(() => import('./pages/Chat'));
const MyAgents = lazy(() => import('./pages/MyAgents'));
const Tasks = lazy(() => import('./pages/Tasks'));
const Workflows = lazy(() => import('./pages/Workflows'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminDepartments = lazy(() => import('./pages/AdminDepartments'));
const AdminAgents = lazy(() => import('./pages/AdminAgentsEnhanced'));
const AdminScopes = lazy(() => import('./pages/AdminScopes'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'));
const SharedChat = lazy(() => import('./pages/SharedChat'));
const AgentBuilder = lazy(() => import('./pages/AgentBuilder'));
const AdminAgentPlatform = lazy(() => import('./pages/AdminAgentPlatform'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    <div>Loading...</div>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
      <Router>
        <AuthProvider>
          {/* Inside AuthProvider so auth failures can raise a toast, and
              outside Suspense so a toast survives a route-level code split. */}
          <ToastProvider>
          {/* Above the routes so the embedded n8n editor survives page changes. */}
          <WorkflowEditorProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
              <Route path="/my-agents" element={<PrivateRoute><MyAgents /></PrivateRoute>} />
              <Route path="/agent-builder" element={<PrivateRoute><AgentBuilder /></PrivateRoute>} />
              <Route path="/tasks" element={<PrivateRoute><Tasks /></PrivateRoute>} />
              <Route path="/workflows" element={<PrivateRoute><Workflows /></PrivateRoute>} />
              <Route path="/admin/dashboard" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
              <Route path="/admin/users" element={<PrivateRoute adminOnly><AdminUsers /></PrivateRoute>} />
              <Route path="/admin/departments" element={<PrivateRoute adminOnly><AdminDepartments /></PrivateRoute>} />
              <Route path="/admin/agents" element={<PrivateRoute adminOnly><AdminAgents /></PrivateRoute>} />
              <Route path="/admin/scopes" element={<PrivateRoute adminOnly><AdminScopes /></PrivateRoute>} />
              <Route path="/admin/agent-platform" element={<PrivateRoute adminOnly><AdminAgentPlatform /></PrivateRoute>} />
              <Route path="/admin/feedback" element={<PrivateRoute adminOnly><AdminFeedback /></PrivateRoute>} />
              <Route path="/admin/announcements" element={<PrivateRoute adminOnly><AdminAnnouncements /></PrivateRoute>} />
              <Route path="/shared/:token" element={<PrivateRoute><SharedChat /></PrivateRoute>} />
              <Route path="/" element={<Navigate to="/my-agents" replace />} />
            </Routes>
          </Suspense>
          </WorkflowEditorProvider>
          </ToastProvider>
        </AuthProvider>
      </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
